/*
===============================================================================
FILE: assets/continue-shopping-segmented.js
TITLE: CONTINUE SHOPPING WIDGET JS
FEATURES:
- Recently viewed collection segments
- Opens clicked product inside popup carousel
- Size-specific stock logic
- Supports Shopify "continue selling when out of stock"
- Shows "Shipped In 7–8 Days" for preorder variants
- Button shows "Preorder" for continue-selling 0 stock variants
- Line item properties + cart attributes tracking
===============================================================================
*/

(function () {
  var STORAGE_KEY = 'an_recent_product_views_v3';
  var MAX_DAYS = 30;

  function readViews() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];

      var now = Date.now();
      var maxAge = MAX_DAYS * 24 * 60 * 60 * 1000;

      return JSON.parse(raw)
        .filter(function (item) {
          return item && item.id && item.primaryCollection && item.timestamp && now - item.timestamp <= maxAge;
        })
        .sort(function (a, b) {
          return b.timestamp - a.timestamp;
        });
    } catch (e) {
      return [];
    }
  }

  function groupViews(views) {
    var groups = {};

    views.forEach(function (product) {
      var key = product.primaryCollection;

      if (!groups[key]) {
        groups[key] = {
          title: key,
          products: [],
          latest: 0
        };
      }

      groups[key].products.push(product);
      groups[key].latest = Math.max(groups[key].latest, product.timestamp);
    });

    return Object.keys(groups)
      .map(function (key) {
        groups[key].products.sort(function (a, b) {
          return b.timestamp - a.timestamp;
        });

        return groups[key];
      })
      .sort(function (a, b) {
        return b.latest - a.latest;
      });
  }

  function formatMoney(cents) {
    if (window.Shopify && Shopify.formatMoney) {
      return Shopify.formatMoney(cents);
    }

    return 'Rs. ' + Math.round(cents / 100).toLocaleString('en-IN') + '.00';
  }

  function escapeHtml(text) {
    return String(text || '').replace(/[&<>"']/g, function (m) {
      return {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
      }[m];
    });
  }

  function normalizeSizeTitle(title) {
    return String(title || '').replace('Default Title', 'Size').trim();
  }

  function splitAttributeList(value) {
    if (!value) return [];

    return String(value)
      .split(',')
      .map(function (item) {
        return item.trim();
      })
      .filter(Boolean);
  }

  function uniquePush(list, value) {
    value = String(value || '').trim();

    if (value && list.indexOf(value) === -1) {
      list.push(value);
    }

    return list;
  }

  function canSellVariant(variant) {
    return Boolean(
      variant &&
      (
        variant.available ||
        variant.inventory_policy === 'continue'
      )
    );
  }

  function isPreorderVariant(variant) {
    return Boolean(
      variant &&
      variant.inventory_policy === 'continue' &&
      Number(variant.inventory_quantity) <= 0
    );
  }

  function getVariantStockState(variant) {
    var size = normalizeSizeTitle(variant.title);
    var qty = Number(variant.inventory_quantity);
    var continueSelling = variant.inventory_policy === 'continue';
    var canSell = canSellVariant(variant);

    if (!canSell) {
      return {
        text: 'Out Of Stock In ' + size,
        color: 'grey',
        canAdd: false,
        isPreorder: false
      };
    }

    if (continueSelling && qty <= 0) {
      return {
        text: 'Shipped In 7–8 Days',
        color: 'orange',
        canAdd: true,
        isPreorder: true
      };
    }

    if (qty >= 10) {
      return {
        text: 'In Stock',
        color: 'green',
        canAdd: true,
        isPreorder: false
      };
    }

    if (qty >= 5 && qty <= 9) {
      return {
        text: 'Only Few Left In ' + size,
        color: 'orange',
        canAdd: true,
        isPreorder: false
      };
    }

    if (qty >= 3 && qty <= 4) {
      return {
        text: 'Only ' + qty + ' Left In ' + size,
        color: 'orange',
        canAdd: true,
        isPreorder: false
      };
    }

    if (qty >= 1 && qty <= 2) {
      return {
        text: 'Only ' + qty + ' Left In ' + size,
        color: 'red',
        canAdd: true,
        isPreorder: false
      };
    }

    return {
      text: 'In Stock',
      color: 'green',
      canAdd: true,
      isPreorder: false
    };
  }

  function renderSegment(group) {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'an-cs__segment';

    var products = group.products.slice(0, 6);
    var styleWord = group.products.length === 1 ? 'Style' : 'Styles';

    button.innerHTML =
      '<div class="an-cs__segment-images">' +
        products.map(function (product, index) {
          return (
            '<img src="' +
            product.image +
            '" alt="' +
            escapeHtml(product.title) +
            '" loading="lazy" data-an-target-index="' +
            index +
            '">'
          );
        }).join('') +
      '</div>' +
      '<div class="an-cs__segment-info">' +
        '<p class="an-cs__segment-title">' + escapeHtml(group.title) + '</p>' +
        '<div class="an-cs__segment-count">' +
          group.products.length + ' ' + styleWord + ' You Checked Out' +
        '</div>' +
      '</div>';

    return button;
  }

  function updateStockLine(stockLine, variant, widget) {
    if (!stockLine || widget.getAttribute('data-enable-stock') !== 'true') return;

    var state = getVariantStockState(variant);

    stockLine.textContent = state.text;
    stockLine.className = 'an-cs-product__stock an-cs-product__stock--' + state.color;
    stockLine.hidden = false;
  }

  function renderProduct(product, segmentTitle, widget) {
    var div = document.createElement('div');
    div.className = 'an-cs-product';

    var variants = product.variants || [];

    var sizeButtons = variants.map(function (variant) {
      return (
        '<button type="button" class="an-cs-product__size" data-variant-id="' +
        variant.id +
        '">' +
        escapeHtml(normalizeSizeTitle(variant.title)) +
        '</button>'
      );
    }).join('');

    div.innerHTML =
      '<a class="an-cs-product__image" href="' + product.url + '">' +
        '<img src="' + product.image + '" alt="' + escapeHtml(product.title) + '" loading="lazy">' +
      '</a>' +
      '<a class="an-cs-product__title" href="' + product.url + '">' +
        escapeHtml(product.title) +
      '</a>' +
      '<div class="an-cs-product__price">' +
        formatMoney(product.price) +
      '</div>' +
      '<div class="an-cs-product__stock an-cs-product__stock--grey" data-stock-line hidden></div>' +
      '<div class="an-cs-product__sizes">' +
        sizeButtons +
      '</div>' +
      '<button type="button" class="an-cs-product__add" data-add disabled>Select Size</button>';

    var selectedVariant = null;
    var addButton = div.querySelector('[data-add]');
    var stockLine = div.querySelector('[data-stock-line]');

    div.querySelectorAll('.an-cs-product__size').forEach(function (btn) {
      var variantId = btn.getAttribute('data-variant-id');

      var variant = variants.find(function (item) {
        return String(item.id) === String(variantId);
      });

      if (!variant) return;

      var state = getVariantStockState(variant);

      if (!state.canAdd) {
        btn.disabled = true;
      }

      btn.addEventListener('click', function () {
        div.querySelectorAll('.an-cs-product__size').forEach(function (b) {
          b.classList.remove('is-selected');
        });

        btn.classList.add('is-selected');
        selectedVariant = variant;

        updateStockLine(stockLine, variant, widget);

        if (state.canAdd) {
          addButton.disabled = false;
          addButton.textContent = state.isPreorder ? 'Preorder' : 'Add To Cart';
        } else {
          addButton.disabled = true;
          addButton.textContent = 'Out Of Stock';
        }
      });
    });

    addButton.addEventListener('click', function () {
      if (!selectedVariant) return;

      addTrackedProduct(selectedVariant.id, selectedVariant, product, segmentTitle, addButton);
    });

    return div;
  }

  function dispatchCartEvents() {
    document.dispatchEvent(new CustomEvent('cart:build'));
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
    document.dispatchEvent(new CustomEvent('cart:updated'));

    document.body.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
    document.body.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.body.dispatchEvent(new CustomEvent('ajaxProduct:added', { bubbles: true }));
    document.body.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
  }

  function openImpulseCartDrawer() {
    var selectors = [
      '.js-drawer-open-cart',
      '[data-cart-drawer-trigger]',
      '[data-drawer-open="CartDrawer"]',
      '[data-drawer-open="cart"]'
    ];

    dispatchCartEvents();

    setTimeout(function () {
      for (var i = 0; i < selectors.length; i++) {
        var trigger = document.querySelector(selectors[i]);

        if (trigger) {
          trigger.click();
          return;
        }
      }

      document.dispatchEvent(new CustomEvent('cart:open'));
      document.body.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
    }, 150);
  }

  function updateCartAttributesInBackground(product, segmentTitle) {
    fetch('/cart.js', {
      headers: {
        Accept: 'application/json'
      }
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        var attributes = cart.attributes || {};

        var segmentsUsed = splitAttributeList(attributes.cs_segments_used);
        var productsAdded = splitAttributeList(attributes.cs_products_added);
        var totalAdds = Number(attributes.cs_total_adds || 0);

        uniquePush(segmentsUsed, segmentTitle);
        uniquePush(productsAdded, product.title);

        totalAdds += 1;

        return fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            attributes: {
              cs_widget_used: 'Yes',
              cs_segments_used: segmentsUsed.join(', '),
              cs_products_added: productsAdded.join(', '),
              cs_total_adds: String(totalAdds)
            }
          })
        });
      })
      .catch(function () {});
  }

  function addTrackedProduct(variantId, variant, product, segmentTitle, button) {
    if (!canSellVariant(variant)) {
      button.textContent = 'Out Of Stock';
      button.disabled = true;
      return;
    }

    var buttonResetText = isPreorderVariant(variant) ? 'Preorder' : 'Add To Cart';

    button.disabled = true;
    button.textContent = 'Adding';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        items: [
          {
            id: Number(variantId),
            quantity: 1,
            properties: {
              _source: 'Continue Shopping Widget',
              _segment: segmentTitle,
              _viewed_product: product.title,
              _widget_action: isPreorderVariant(variant) ? 'Recently Viewed Preorder' : 'Recently Viewed Quick Add',
              _selected_size: normalizeSizeTitle(variant.title),
              _fulfillment_message: isPreorderVariant(variant) ? 'Shipped In 7–8 Days' : ''
            }
          }
        ]
      })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Add failed');
        return res.json();
      })
      .then(function () {
        button.textContent = 'Added';

        openImpulseCartDrawer();
        updateCartAttributesInBackground(product, segmentTitle);

        setTimeout(function () {
          button.textContent = buttonResetText;
          button.disabled = false;
        }, 700);
      })
      .catch(function () {
        button.textContent = 'Try Again';
        button.disabled = false;
      });
  }

  function scrollCarouselToProduct(productsContainer, index) {
    if (!productsContainer) return;

    var cards = productsContainer.querySelectorAll('.an-cs-product');

    if (!cards.length) {
      productsContainer.scrollLeft = 0;
      return;
    }

    var safeIndex = Math.max(0, Math.min(Number(index) || 0, cards.length - 1));
    var targetCard = cards[safeIndex];

    productsContainer.scrollLeft = 0;

    requestAnimationFrame(function () {
      targetCard.scrollIntoView({
        behavior: 'auto',
        block: 'nearest',
        inline: 'center'
      });
    });

    setTimeout(function () {
      targetCard.scrollIntoView({
        behavior: 'auto',
        block: 'nearest',
        inline: 'center'
      });
    }, 80);
  }

  function openModal(widget, group, targetIndex) {
    var modal = widget.querySelector('[data-an-cs-modal]');
    var title = widget.querySelector('[data-an-cs-modal-title]');
    var products = widget.querySelector('[data-an-cs-modal-products]');
    var maxProducts = Number(widget.getAttribute('data-max-products')) || 8;

    title.textContent = group.title;
    products.innerHTML = '';

    group.products.slice(0, maxProducts).forEach(function (product) {
      products.appendChild(renderProduct(product, group.title, widget));
    });

    modal.hidden = false;
    document.documentElement.classList.add('js-drawer-open');

    scrollCarouselToProduct(products, targetIndex);
  }

  function closeModal(widget) {
    var modal = widget.querySelector('[data-an-cs-modal]');
    modal.hidden = true;
    document.documentElement.classList.remove('js-drawer-open');
  }

  function initWidget(widget) {
    var views = readViews();
    var groups = groupViews(views);
    var maxSegments = Number(widget.getAttribute('data-max-segments')) || 6;
    var container = widget.querySelector('[data-an-cs-segments]');

    if (!groups.length) {
      widget.hidden = true;
      return;
    }

    widget.hidden = false;
    container.innerHTML = '';

    groups.slice(0, maxSegments).forEach(function (group) {
      var segment = renderSegment(group);

      segment.addEventListener('click', function (event) {
        var clickedImage = event.target.closest('[data-an-target-index]');
        var targetIndex = clickedImage ? Number(clickedImage.getAttribute('data-an-target-index')) : 0;

        openModal(widget, group, targetIndex);
      });

      container.appendChild(segment);
    });

    widget.querySelectorAll('[data-an-cs-close]').forEach(function (close) {
      close.addEventListener('click', function () {
        closeModal(widget);
      });
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-an-cs]').forEach(initWidget);
  });
})();